import React from "react";
import { Shield, AlertTriangle, Ban, Cigarette, Wine, FileText, Users, Clock, Phone, Scale, Leaf, Home, CreditCard, Car } from "lucide-react";
import PageHero from "../components/blocks/PageHero";
import Section from "../components/ui/Section";
import useSEO from "../hooks/useSEO";

// Policy section component
const PolicySection = ({ icon: Icon, title, children, variant = "default" }) => {
  const variants = {
    default: "border-white/10 bg-white/5",
    warning: "border-amber-500/30 bg-amber-500/10",
    danger: "border-red-500/30 bg-red-500/10",
    info: "border-blue-500/30 bg-blue-500/10"
  };
  
  const iconColors = {
    default: "text-[#C9A227]",
    warning: "text-amber-400",
    danger: "text-red-400",
    info: "text-blue-400"
  };

  return (
    <div className={`rounded-2xl border p-6 md:p-8 ${variants[variant]}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-xl bg-black/30 border border-white/10 ${iconColors[variant]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <h3 className="text-xl md:text-2xl font-light text-white pt-2">{title}</h3>
      </div>
      <div className="text-white/70 text-sm md:text-base leading-relaxed space-y-4">
        {children}
      </div>
    </div>
  );
};

// Policy item component
const PolicyItem = ({ children }) => (
  <div className="flex items-start gap-3">
    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A227] mt-2 flex-shrink-0" />
    <span>{children}</span>
  </div>
);

const Policies = () => {
  useSEO('/policies');
  
  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="Resort Policies & Guidelines"
        subtitle="Ensuring a safe, comfortable, and memorable stay for all"
        image="https://images.unsplash.com/photo-1586611292717-f828b167408c?auto=format&fit=crop&w=2400&q=80"
      />

      {/* Introduction */}
      <Section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 mb-8">
            <Shield className="w-4 h-4 text-[#C9A227]" />
            <span className="text-xs uppercase tracking-widest text-[#C9A227]">Official Resort Policies</span>
          </div>
          
          <h2 className="text-2xl md:text-4xl font-light text-white mb-6">
            Welcome to <span className="text-[#C9A227]">Glow LAWNS</span>
          </h2>
          
          <p className="text-white/70 text-base leading-relaxed max-w-3xl mx-auto">
            At Glow LAWNS Moonrise Valley, we are committed to providing a safe, peaceful, and enjoyable 
            experience for all our guests. These policies are established in accordance with the 
            <span className="text-white"> Kerala Tourism Department guidelines</span>, 
            <span className="text-white"> Government of India regulations</span>, and local administrative requirements 
            to ensure compliance and the well-being of our guests, staff, and the local community.
          </p>
        </div>
      </Section>

      {/* Main Policies Grid */}
      <Section className="py-8">
        <div className="max-w-5xl mx-auto space-y-8">
          
          {/* General Resort Rules */}
          <PolicySection icon={Home} title="General Resort Rules & Guidelines">
            <p className="mb-4">
              As a registered accommodation establishment under the <strong>Kerala Tourism (Conservation and Preservation of Areas) Rules</strong> 
              and licensed by the <strong>Kozhikode District Administration</strong>, we maintain the following standards:
            </p>
            
            <div className="space-y-3">
              <PolicyItem>
                <strong>Check-in Time:</strong> 14:00 hrs (2:00 PM) | <strong>Check-out Time:</strong> 11:00 hrs (11:00 AM). 
                Early check-in or late check-out subject to availability and additional charges.
              </PolicyItem>
              <PolicyItem>
                <strong>Valid ID Required:</strong> All guests must present valid government-issued photo identification 
                (Aadhaar Card, Passport, Driving License, Voter ID) at check-in as per the 
                <strong> Foreigners (Stay in Hotels/Residence) Registration Rules</strong>.
              </PolicyItem>
              <PolicyItem>
                <strong>Guest Registration:</strong> All guests including accompanying family members must be registered. 
                Visitor entry is not permitted without prior approval from management.
              </PolicyItem>
              <PolicyItem>
                <strong>Occupancy Limits:</strong> Maximum occupancy per accommodation type must be strictly adhered to 
                as per fire safety regulations. Extra guests require prior approval and additional charges.
              </PolicyItem>
              <PolicyItem>
                <strong>Quiet Hours:</strong> 22:00 hrs to 07:00 hrs. Guests are requested to maintain silence 
                and avoid loud music or disturbances during these hours out of respect for fellow guests and wildlife.
              </PolicyItem>
              <PolicyItem>
                <strong>Children:</strong> Children below 12 years must be accompanied by adults at all times. 
                The resort is not responsible for unsupervised minors.
              </PolicyItem>
              <PolicyItem>
                <strong>Pets:</strong> Pets are not allowed on the premises to maintain hygiene and avoid 
                disturbance to wildlife in the surrounding forest area.
              </PolicyItem>
            </div>
          </PolicySection>

          {/* No Smoking Policy */}
          <PolicySection icon={Cigarette} title="Strict No Smoking Policy" variant="warning">
            <p className="mb-4">
              In compliance with the <strong>Cigarettes and Other Tobacco Products Act (COTPA), 2003</strong> and 
              <strong> Kerala Prohibition of Smoking and Spitting Act, 2012</strong>, Glow LAWNS enforces a 
              <strong className="text-amber-400"> 100% SMOKE-FREE ENVIRONMENT</strong>.
            </p>
            
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
              <p className="text-amber-300 font-medium mb-2">Smoking is STRICTLY PROHIBITED in:</p>
              <ul className="text-white/70 space-y-1 text-sm">
                <li>• All accommodation units (Tree Houses, Stone House, Mud House)</li>
                <li>• Restaurant and dining areas</li>
                <li>• Common areas, lobbies, and reception</li>
                <li>• Balconies, decks, and outdoor seating areas</li>
                <li>• Pathways and garden areas</li>
                <li>• Parking areas and entry points</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <PolicyItem>
                This includes cigarettes, beedis, cigars, e-cigarettes, vapes, hookah, and all tobacco products.
              </PolicyItem>
              <PolicyItem>
                <strong>Penalty:</strong> Violation may result in a fine of <strong>₹200 to ₹500</strong> as per COTPA Section 4, 
                and immediate eviction without refund.
              </PolicyItem>
              <PolicyItem>
                <strong>Fire Hazard:</strong> Being located in a forest area, smoking poses extreme fire risk. 
                Violators may be reported to the <strong>Kerala Forest Department</strong> and local authorities.
              </PolicyItem>
              <PolicyItem>
                <strong>Designated Area:</strong> A designated smoking area may be provided at a safe distance 
                from buildings upon request. Please contact reception.
              </PolicyItem>
            </div>
          </PolicySection>

          {/* Drug & Alcohol Policy */}
          <PolicySection icon={Wine} title="Drug & Alcohol Policy" variant="danger">
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-4">
              <p className="text-red-300 font-semibold text-lg mb-2">
                ZERO TOLERANCE POLICY
              </p>
              <p className="text-white/70">
                Glow LAWNS Moonrise Valley is a <strong className="text-red-300">DRUG-FREE and ALCOHOL-FREE establishment</strong>. 
                We do not serve, sell, or permit the consumption of alcoholic beverages or any narcotic/psychotropic substances 
                on our premises.
              </p>
            </div>
            
            <div className="space-y-3">
              <PolicyItem>
                <strong>No Alcohol Service:</strong> The resort, its management, owners, and staff do NOT serve, 
                sell, or provide any alcoholic beverages including beer, wine, spirits, or country liquor.
              </PolicyItem>
              <PolicyItem>
                <strong>BYOB Not Permitted:</strong> Guests are NOT permitted to bring, store, or consume alcohol 
                anywhere on the resort premises including private accommodations.
              </PolicyItem>
              <PolicyItem>
                <strong>Narcotic Substances:</strong> Possession, consumption, or distribution of any narcotic drugs, 
                psychotropic substances, marijuana, ganja, or controlled substances is <strong>STRICTLY ILLEGAL</strong> under 
                the <strong>Narcotic Drugs and Psychotropic Substances (NDPS) Act, 1985</strong>.
              </PolicyItem>
              <PolicyItem>
                <strong>Legal Consequences:</strong> Violation will result in immediate eviction, forfeiture of all payments, 
                and reporting to the <strong>Kerala Police</strong> and <strong>Excise Department</strong>. 
                NDPS Act violations carry imprisonment from 10 years to 20 years and fines up to ₹2 lakhs.
              </PolicyItem>
              <PolicyItem>
                <strong>Guest Conduct:</strong> Guests appearing intoxicated or under the influence of substances 
                will be denied entry or asked to vacate immediately without refund.
              </PolicyItem>
              <PolicyItem>
                This policy applies to all guests, visitors, staff, vendors, and any person entering the premises.
              </PolicyItem>
            </div>
            
            <p className="mt-4 text-white/50 text-sm italic">
              This policy is maintained to ensure the safety of our guests, preserve the serene environment of the resort, 
              and comply with Kerala Abkari (Excise) Act regulations for unlicensed establishments.
            </p>
          </PolicySection>

          {/* Damage & Misconduct Policy */}
          <PolicySection icon={AlertTriangle} title="Damage & Misconduct Policy" variant="warning">
            <p className="mb-4">
              Guests are expected to treat the resort property, furnishings, and natural surroundings with care and respect. 
              The following guidelines are enforced to protect our property and ensure safety:
            </p>
            
            <div className="space-y-3">
              <PolicyItem>
                <strong>Property Damage:</strong> Guests will be held financially responsible for any damage caused to 
                resort property, furniture, fixtures, linens, or equipment during their stay. Damage assessment will 
                be conducted by management, and charges will be added to the final bill or deducted from security deposit.
              </PolicyItem>
              <PolicyItem>
                <strong>Security Deposit:</strong> A refundable security deposit may be collected at check-in. 
                This will be refunded after room inspection at check-out, minus any damage charges.
              </PolicyItem>
              <PolicyItem>
                <strong>Natural Environment:</strong> Defacing, cutting, or damaging trees, plants, or natural features 
                is prohibited and punishable under the <strong>Kerala Forest Act, 1961</strong> and environmental protection laws.
              </PolicyItem>
              <PolicyItem>
                <strong>Wildlife:</strong> Feeding, disturbing, or harming wildlife is strictly prohibited under the 
                <strong> Wildlife Protection Act, 1972</strong>. Report any wildlife sightings to staff.
              </PolicyItem>
              <PolicyItem>
                <strong>Misconduct:</strong> Disorderly conduct, harassment of staff or guests, use of abusive language, 
                fighting, or any behavior causing nuisance will result in immediate eviction without refund and 
                may be reported to local police.
              </PolicyItem>
              <PolicyItem>
                <strong>Illegal Activities:</strong> Any illegal activities including gambling, solicitation, or 
                criminal behavior will be reported to authorities immediately.
              </PolicyItem>
              <PolicyItem>
                <strong>Fire Safety:</strong> Tampering with fire safety equipment, blocking fire exits, or 
                causing fire hazards is a punishable offense under the <strong>Kerala Fire Force Act</strong>.
              </PolicyItem>
            </div>
          </PolicySection>

          {/* Payment & Cancellation */}
          <PolicySection icon={CreditCard} title="Payment & Cancellation Policy">
            <div className="space-y-3">
              <PolicyItem>
                <strong>Advance Payment:</strong> 50% advance payment required at the time of booking. 
                Balance to be paid at check-in.
              </PolicyItem>
              <PolicyItem>
                <strong>Payment Methods:</strong> We accept Cash, UPI, Bank Transfer, and major Debit/Credit Cards. 
                GST as applicable will be charged as per Government of India norms.
              </PolicyItem>
              <PolicyItem>
                <strong>Cancellation:</strong> Free cancellation up to 7 days before check-in. 50% refund for 
                cancellations 3-7 days prior. No refund for cancellations within 48 hours or no-shows.
              </PolicyItem>
              <PolicyItem>
                <strong>Modification:</strong> Date changes subject to availability and may incur price differences 
                based on seasonal rates.
              </PolicyItem>
              <PolicyItem>
                <strong>Force Majeure:</strong> In case of natural disasters, government restrictions, or unforeseen 
                circumstances, booking may be rescheduled without penalty.
              </PolicyItem>
            </div>
          </PolicySection>

          {/* Safety & Liability */}
          <PolicySection icon={Shield} title="Safety & Liability Disclaimer">
            <div className="space-y-3">
              <PolicyItem>
                <strong>Personal Belongings:</strong> The resort is not responsible for loss, theft, or damage to 
                guest's personal belongings, valuables, or vehicles. Safe deposit facilities available at reception.
              </PolicyItem>
              <PolicyItem>
                <strong>Natural Hazards:</strong> Being located in a forest area, guests should be aware of 
                natural terrain, wildlife, and weather conditions. Follow staff instructions during monsoon or 
                adverse weather.
              </PolicyItem>
              <PolicyItem>
                <strong>Medical Emergencies:</strong> Basic first aid available. Nearest hospital is approximately 
                15 km away. Guests with medical conditions should inform staff and carry necessary medications.
              </PolicyItem>
              <PolicyItem>
                <strong>Adventure Activities:</strong> Participation in off-roading, trekking, or outdoor activities 
                is at guest's own risk. Liability waiver may be required.
              </PolicyItem>
              <PolicyItem>
                <strong>Photography:</strong> Commercial photography or filming requires prior written permission 
                from management.
              </PolicyItem>
            </div>
          </PolicySection>

          {/* Compliance Statement */}
          <div className="p-6 md:p-8 rounded-2xl border border-[#C9A227]/30 bg-gradient-to-br from-[#C9A227]/10 to-[#C9A227]/5">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-xl bg-[#C9A227]/20 border border-[#C9A227]/30">
                <Scale className="w-6 h-6 text-[#C9A227]" />
              </div>
              <div>
                <h3 className="text-xl font-light text-white mb-2">Regulatory Compliance</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Glow LAWNS Moonrise Valley operates in full compliance with:
                </p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4 text-sm text-white/60 mt-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C9A227]" />
                <span>FSSAI Food Safety License</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C9A227]" />
                <span>Panchayat Building Approval</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C9A227]" />
                <span>Pollution Prevention Certificate</span>
              </div>
            </div>
          </div>

          {/* Contact for Queries */}
          <div className="text-center py-8">
            <p className="text-white/60 mb-4">
              For any clarifications regarding our policies, please contact:
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="tel:+919995450101" className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white/80 hover:border-[#C9A227]/50 hover:text-[#C9A227] transition">
                <Phone className="w-4 h-4" />
                +91-9995450101
              </a>
              <a href="mailto:bookingglowlawns@gmail.com" className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white/80 hover:border-[#C9A227]/50 hover:text-[#C9A227] transition">
                <FileText className="w-4 h-4" />
                bookingglowlawns@gmail.com
              </a>
            </div>
            <p className="text-white/40 text-xs mt-6 max-w-2xl mx-auto">
              By making a reservation or staying at Glow LAWNS Moonrise Valley, guests acknowledge that they have 
              read, understood, and agree to abide by all the above policies and guidelines. Management reserves 
              the right to update these policies as required by law or operational needs.
            </p>
            <p className="text-white/30 text-xs mt-4">
              Last Updated: February 2025 | Version 1.0
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Policies;
